import { useEffect, useRef, useState } from 'react';
import { VaultGate } from '@/components/VaultGate';
import { api, type HttpError } from '@/lib/api';
import { type BrokerName, isBrokerName, withBroker } from '@/lib/brokerCreds';

interface L2 {
  symbol:    string;
  bids:      Array<{ price: number | null; size: number; exchange?: string }>;
  asks:      Array<{ price: number | null; size: number; exchange?: string }>;
  timestamp: number;
  feed:      string;
  broker?:   string;
}

/**
 * Level2 — bid/ask depth ladder. Polls /api/level2/:symbol every 2s.
 * Free Alpaca IEX feed gives top-of-book; Algo Trader Plus (SIP feed)
 * returns deeper depth via the same shape.
 */
export function Level2() {
  const [symbolInput, setSymbolInput] = useState('AAPL');
  const [symbol,      setSymbol] = useState('AAPL');
  const [book,        setBook] = useState<L2 | null>(null);
  const [err,         setErr] = useState<string | null>(null);
  // Venue by name, learned from the router's answer (see DomTab): the first
  // poll carries no credential, later polls ask for that venue and carry
  // only its credential. Never a guessed venue.
  // A ref, not state: learning the venue must not re-run the polling effect
  // (which clears the book) -- it only changes the next request.
  const venueRef = useRef<BrokerName | null>(null);

  useEffect(() => {
    let alive = true;
    // CLEAR THE PREVIOUS SYMBOL'S BOOK FIRST. Without this, switching tickers
    // (or a failed fetch for the new one) left the OLD symbol's full depth
    // ladder on screen under the NEW symbol's header -- bids and asks that
    // belong to a different instrument, which is worse than an empty ladder.
    setBook(null);
    setErr(null);
    venueRef.current = null;
    const tick = async () => {
      try {
        // Two bugs: raw fetch() sent no Authorization header (the endpoint
        // requires a Passport JWT), and the response was never checked for
        // r.ok — so a 401/502 body like {error:"..."} was set as the order
        // book AND cleared the error banner. The ladder then rendered
        // empty, reading as "no depth available" rather than "the feed is
        // down or you are not authorised".
        const d = await api<L2>(withBroker(`/api/level2/${encodeURIComponent(symbol)}`, venueRef.current));
        if (alive) {
          setBook(d); setErr(null);
          if (isBrokerName(d?.broker)) venueRef.current = d.broker;
        }
      } catch (e) {
        // Keep the last good book on screen but SAY it is stale, rather
        // than silently showing old depth as if it were live.
        const hb = (e as HttpError)?.body?.broker;
        if (alive && isBrokerName(hb)) venueRef.current = hb;
        if (alive) setErr((e as Error).message || String(e));
      }
    };
    tick();
    const h = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(h); };
  }, [symbol]);

  const maxSize = Math.max(1, ...(book?.bids ?? []).map(b => b.size), ...(book?.asks ?? []).map(a => a.size));

  return (
    <div style={{ padding: '20px 24px', color: '#fafaf9', fontFamily: 'Inter, sans-serif' }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 500 }}>Level 2</h2>
      <div style={{ color: '#a8a29e', fontSize: 12, marginBottom: 14 }}>
        Feed: <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{book?.feed ?? '...'}</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <input
          value={symbolInput}
          onChange={e => setSymbolInput(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === 'Enter') setSymbol(symbolInput); }}
          placeholder="Symbol"
          style={{ background: '#292524', border: '1px solid rgba(245,244,238,0.10)', color: '#fafaf9', padding: '6px 10px', borderRadius: 4, fontSize: 13 }}
        />
        <button onClick={() => setSymbol(symbolInput)} style={{ padding: '6px 14px', background: '#d4a45a', color: '#0c0a09', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Load</button>
        {err && <span style={{ color: '#fca5a5', fontSize: 12 }}>{err.slice(0, 80)}</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ color: '#4ade80', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Bids</div>
          {(book?.bids ?? []).map((b, i) => (
            <div key={'b'+i} style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '5px 8px', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, marginBottom: 2 }}>
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(105,176,120,0.10)', width: `${(b.size / maxSize) * 100}%`, borderRadius: 3 }} />
              <div style={{ position: 'relative', color: '#4ade80' }}>{typeof b.price === 'number' ? b.price.toFixed(2) : '\u2014'}</div>
              <div style={{ position: 'relative', textAlign: 'right' }}>{b.size}</div>
              <div style={{ position: 'relative', textAlign: 'right', color: '#a8a29e' }}>{b.exchange ?? ''}</div>
            </div>
          ))}
        </div>
        <div>
          <div style={{ color: '#e8b5a8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Asks</div>
          {(book?.asks ?? []).map((a, i) => (
            <div key={'a'+i} style={{ position: 'relative', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '5px 8px', fontFamily: 'JetBrains Mono, monospace', fontSize: 12, marginBottom: 2 }}>
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(232,181,168,0.10)', width: `${(a.size / maxSize) * 100}%`, borderRadius: 3 }} />
              <div style={{ position: 'relative', color: '#e8b5a8' }}>{typeof a.price === 'number' ? a.price.toFixed(2) : '\u2014'}</div>
              <div style={{ position: 'relative', textAlign: 'right' }}>{a.size}</div>
              <div style={{ position: 'relative', textAlign: 'right', color: '#a8a29e' }}>{a.exchange ?? ''}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Level2Page() {
  return (
    <VaultGate silent>
      <Level2 />
    </VaultGate>
  );
}
